import {Box, Typography} from '@mui/material';
import {Page, PageSeo, PageType} from '../../components/common/container';
import {Prose, SectionTitle, Title} from '../../components/common/text';
import {Trans, useTranslation} from 'next-i18next';
import {GetStaticProps} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import {CONTACT, KUNEKUNE_INFO} from '../../constants';

export const getStaticProps: GetStaticProps<{}> = async (context) => {
    const translation = await serverSideTranslations(context.locale, [
        'common',
        'privacy-policy',
    ]);
    return {
        props: translation,
        revalidate: 86400, // 1 jour
    };
};

export default function Cgu() {
    const {t} = useTranslation('privacy-policy');

    return (
        <Page>
            <PageSeo page={PageType.CGU} />
            <Title>{t('title')}</Title>
            <Box
                sx={{
                    maxWidth: 550,
                    marginRight: 'auto',
                    marginLeft: 'auto',
                    paddingLeft: 4,
                    paddingRight: 4,
                }}
            >
                <Typography variant="caption">{t('updated')}</Typography>
                <Introduction />
                <FirstArticle />
                <SecondArticle />
                <ThirdArticle />
                <FourthArticle />
                <FifthArticle />
                <SixthArticle />
                <SeventhArticle />
                <EightArticle />
                <NinthArticle />
                <TenthArticle />
                <TenPointOneArticle />
                <TenPointTwoArticle />
                <TenPointThreeArticle />
            </Box>
        </Page>
    );
}

function Introduction() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <Prose>{t('content.1')}</Prose>
            <Prose>
                <Trans t={t} i18nKey="content.2" />
            </Prose>
            <Prose>{t('content.3')}</Prose>
            <Prose>{t('content.4')}</Prose>
            <Prose>{t('content.5')}</Prose>
        </>
    );
}

function FirstArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>1. {t('article.1.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.1.content.1" />
            </Prose>
            <ul>
                <li>
                    <Trans t={t} i18nKey="article.1.content.1-list.1" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.1.content.1-list.2" />
                </li>
            </ul>
            <Prose>
                <Trans
                    t={t}
                    i18nKey="article.1.content.2"
                    components={{CgvLink: <Link href="/info/cgv" />}}
                />
            </Prose>
            <Prose>{t('article.1.content.3')}</Prose>
        </>
    );
}

function SecondArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>2. {t('article.2.title')}</SectionTitle>
            <Prose>
                <Trans t={t} i18nKey="article.2.content.1" />
            </Prose>
            <Box
                sx={{
                    paddingRight: 3,
                    paddingLeft: 3,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginTop: 3,
                    marginBottom: 3,
                    textAlign: 'center',
                }}
            >
                <Typography sx={{fontWeight: 'bold'}}>KUNEKUNE</Typography>
                <Typography>{t('article.2.content.kunekune.sas')}</Typography>
                <Typography>
                    {t('article.2.content.kunekune.id', {
                        shortRegistrar: KUNEKUNE_INFO.REGISTRAR_SHORT,
                        id: KUNEKUNE_INFO.ID,
                    })}
                </Typography>
                <Typography>
                    {t('article.2.content.kunekune.address', {
                        address: KUNEKUNE_INFO.ADDRESS,
                    })}
                </Typography>
            </Box>
            <Prose>{t('article.2.content.2', {rgpdEmail: CONTACT.RGPD_EMAIL})}</Prose>
        </>
    );
}

function ThirdArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>3. {t('article.3.title')}</SectionTitle>
            <Prose>{t('article.3.content')}</Prose>
            <ul>
                <li>{t('article.3.list.1')}</li>
                <li>{t('article.3.list.2')}</li>
                <li>{t('article.3.list.3')}</li>
                <li>{t('article.3.list.4')}</li>
                <li>{t('article.3.list.5')}</li>
            </ul>
            <Prose>{t('article.3.pros')}</Prose>
            <ul>
                <li>{t('article.3.list-pros.1')}</li>
            </ul>
        </>
    );
}

function FourthArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>4. {t('article.4.title')}</SectionTitle>
            <Prose>{t('article.4.content')}</Prose>
            <ul>
                <li>{t('article.4.list.1')}</li>
                <li>{t('article.4.list.2')}</li>
                <li>{t('article.4.list.3')}</li>
            </ul>
        </>
    );
}

function FifthArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>5. {t('article.5.title')}</SectionTitle>
            <Prose>{t('article.5.content')}</Prose>
            <ul>
                <li>{t('article.5.list.1')}</li>
                <li>{t('article.5.list.2')}</li>
                <li>{t('article.5.list.3')}</li>
                <li>{t('article.5.list.4')}</li>
            </ul>
        </>
    );
}

function SixthArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>6. {t('article.6.title')}</SectionTitle>
            <Prose>{t('article.6.content.1')}</Prose>
            <Prose>{t('article.6.content.2')}</Prose>
        </>
    );
}

function SeventhArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>7. {t('article.7.title')}</SectionTitle>
            <Prose>{t('article.7.content.1')}</Prose>
            <ul>
                <li>{t('article.7.content.1-list.1')}</li>
                <li>{t('article.7.content.1-list.2')}</li>
                <li>{t('article.7.content.1-list.3')}</li>
                <li>{t('article.7.content.1-list.4')}</li>
            </ul>
            <Prose>{t('article.7.content.2')}</Prose>
        </>
    );
}

function EightArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>8. {t('article.8.title')}</SectionTitle>
            <Prose>{t('article.8.content.1')}</Prose>
            <ol>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.1" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.2" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.3" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.4" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.5" />
                </li>
                <li>
                    <Trans t={t} i18nKey="article.8.content.1-list.6" />
                </li>
            </ol>
            <Prose>
                <Trans t={t} i18nKey="article.8.content.2" />
            </Prose>
            <Prose>
                <Trans t={t} i18nKey="article.8.content.3" />
            </Prose>
            <ul>
                <li>
                    <Trans
                        t={t}
                        i18nKey="article.8.content.3-list.1"
                        values={{rgpdEmail: CONTACT.RGPD_EMAIL}}
                    />
                </li>
                <li>
                    <Trans
                        t={t}
                        i18nKey="article.8.content.3-list.2"
                        values={{address: KUNEKUNE_INFO.ADDRESS}}
                    />
                </li>
            </ul>
        </>
    );
}

function NinthArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>9. {t('article.9.title')}</SectionTitle>
            <Prose>{t('article.9.content')}</Prose>
            <ul>
                <li>{t('article.9.list.1')}</li>
                <li>{t('article.9.list.2')}</li>
                <li>{t('article.9.list.3')}</li>
            </ul>
        </>
    );
}

function TenthArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>10. {t('article.10.title')}</SectionTitle>
            <Prose>{t('article.10.content')}</Prose>
        </>
    );
}

function TenPointOneArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>10.1. {t('article.10-1.title')}</SectionTitle>
            <Prose>{t('article.10-1.content')}</Prose>
            <ul>
                <li>{t('article.10-1.list.1')}</li>
            </ul>
        </>
    );
}

function TenPointTwoArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>10.2. {t('article.10-2.title')}</SectionTitle>
            <Prose>{t('article.10-2.content')}</Prose>
        </>
    );
}

const CNIL_URL_COOKIES =
    'https://www.cnil.fr/fr/cookies-et-autres-traceurs/comment-se-proteger/maitriser-votre-navigateur';

function TenPointThreeArticle() {
    const {t} = useTranslation('privacy-policy');

    return (
        <>
            <SectionTitle>10.3. {t('article.10-3.title')}</SectionTitle>
            <Prose>{t('article.10-3.content.1')}</Prose>
            <Prose>{t('article.10-3.content.2')}</Prose>
            <Trans
                t={t}
                i18nKey="article.10-3.content.3"
                components={{
                    cnilLink: <Link href="CNIL_URL_COOKIES">{CNIL_URL_COOKIES}</Link>,
                }}
            />
        </>
    );
}
